import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { CloudOff, RefreshCw } from 'lucide-react';
import { flushOutbox, removeOutboxEntry } from '../lib/outbox';
import { useOutboxEntries, useOutboxEntry } from '../hooks/useOutbox';
import { ConfirmDialog } from './ConfirmDialog';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import { documentTitle } from '../lib/docView';

/**
 * The offline-save surfaces (ask-first conflicts — see lib/outbox.ts).
 *
 * `DocumentOutboxBanner` sits on the document page: a quiet status line
 * while an offline edit waits to sync, and an attention banner when the
 * flush hit a CONFLICT — the user reviews their version in the editor or
 * discards it; the app never picks a winner for them.
 *
 * `OutboxSummaryBanner` sits on the Documents page, following the git-sync
 * idiom: ONE calm line for waiting changes, one attention line linking to
 * any document that needs a decision — never per-row noise.
 */

const PENDING_BANNER_CLASSES =
  'glass-panel rounded-xl border border-[var(--card-border)] px-4 py-2.5 text-sm text-[var(--text-secondary)]';
const ATTENTION_BANNER_CLASSES =
  'glass-panel rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-800';

interface DocumentOutboxBannerProps {
  path: string;
  /** Open the inline editor with the queued offline text as the draft. */
  onReview: (queuedContent: string) => void;
}

/** Offline-save status for ONE document, on its page. */
export function DocumentOutboxBanner({ path, onReview }: DocumentOutboxBannerProps) {
  const client = useApolloClient();
  const entry = useOutboxEntry(path);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (!entry) return null;

  if (entry.status === 'pending') {
    return (
      <div
        role="status"
        data-testid="outbox-pending-banner"
        className={`${PENDING_BANNER_CLASSES} flex items-center justify-between gap-3`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <CloudOff className="w-4 h-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
          Saved on this device — it will sync when you're back online.
        </span>
        <button
          type="button"
          onClick={() => void flushOutbox(client)}
          className={`btn btn-ghost text-sm gap-1.5 shrink-0 ${FOCUS_RING_CLASSES}`}
        >
          <RefreshCw className="w-3.5 h-3.5" aria-hidden /> Sync now
        </button>
      </div>
    );
  }

  return (
    <>
      <div role="alert" data-testid="outbox-conflict-banner" className={ATTENTION_BANNER_CLASSES}>
        <p className="font-semibold">This document changed while your edit waited to sync</p>
        <p className="mt-1">
          To avoid overwriting that change, the version saved on this device wasn't applied. Review
          it against the latest text, or discard it.
        </p>
        <div className="flex gap-2 justify-end mt-3">
          <button
            type="button"
            onClick={() => setConfirmDiscard(true)}
            className={`btn btn-ghost text-sm ${FOCUS_RING_CLASSES}`}
          >
            Discard it
          </button>
          <button
            type="button"
            onClick={() => onReview(entry.content)}
            className={`btn btn-primary text-sm ${FOCUS_RING_CLASSES}`}
          >
            Review your version
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={confirmDiscard}
        title="Discard your offline edit?"
        body="The version saved on this device will be removed. The document keeps what's on the server — nothing else changes."
        confirmLabel="Discard it"
        cancelLabel="Keep it"
        tone="destructive"
        onConfirm={() => {
          removeOutboxEntry(path);
          setConfirmDiscard(false);
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
}

/** One calm line on the Documents page while offline edits wait or need review. */
export function OutboxSummaryBanner() {
  const entries = useOutboxEntries();
  if (entries.length === 0) return null;

  const conflicts = entries.filter((e) => e.status === 'conflict');
  const pendingCount = entries.length - conflicts.length;

  return (
    <div className="space-y-2" data-testid="outbox-summary">
      {pendingCount > 0 && (
        <p role="status" className={`${PENDING_BANNER_CLASSES} flex items-center gap-2`}>
          <CloudOff className="w-4 h-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
          {pendingCount === 1
            ? "One change is saved on this device and will sync when you're back online."
            : `${pendingCount} changes are saved on this device and will sync when you're back online.`}
        </p>
      )}
      {conflicts.length > 0 && (
        <div role="alert" className={ATTENTION_BANNER_CLASSES}>
          <p>
            {conflicts.length === 1
              ? 'An offline edit needs your attention:'
              : 'Some offline edits need your attention:'}{' '}
            {conflicts.map((entry, i) => (
              <span key={entry.path}>
                {i > 0 && ', '}
                <Link
                  to={`/docs/${entry.path}`}
                  className={`underline font-medium ${FOCUS_RING_CLASSES}`}
                >
                  {documentTitle(entry.path, entry.content)}
                </Link>
              </span>
            ))}
            . Your documents are safe either way.
          </p>
        </div>
      )}
    </div>
  );
}
